import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Modal from '../Modals/Modal';
import Text from '../Text/Text';
import Button from '../Button';
import CheckboxInput from '../CheckboxInput';
import { useRef, useState } from 'react';
import { BN, BigNum, QUOTE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import { useEffect } from 'react';
import { Fireworks } from '@fireworks-js/react';
import type { FireworksHandlers } from '@fireworks-js/react';
import { twMerge } from 'tailwind-merge';
import CheckIcon from '../Icons/CheckIcon';
import useSafePush from 'src/hooks/useSafePush';
import { BASE_NAVIGATION_OPTIONS, RouteNavigationResult } from '../Navigation';
import useDriftActions from 'src/hooks/useDriftActions';
import { DRIFT_COMPETITION_PUBKEY } from 'src/constants/sweepstakes';
import DoubleBorderContainer from './DoubleBorderContainer';
import { Close } from '@drift-labs/icons';
import useSweepstakesStore from 'src/stores/useSweepstakesStore';
import Countup from '../Utils/Countup';
import Utility from '../Inputs/Utility';
import { useShallow } from 'zustand/react/shallow';
import useIfDataStore from '../../stores/ifDataStore/useIfDataStore';

const FIREWORKS_DURATION_MS = 5000;

/** Prize position is zero-based index */
const PrizePosition = ({ position }: { position: number }) => {
	const getWording = () => {
		switch (position) {
			case 0:
				return '🥇 First Place';
			case 1:
				return '🥈 Second Place';
			case 2:
				return '🥉 Third Place';
			default:
				return '⭐ Consolation Prize';
		}
	};

	const getTextColorClass = () => {
		switch (position) {
			case 0:
				return 'text-[#FFCF96]';
			case 1:
				return 'text-[#ECECEC]';
			case 2:
				return 'text-[#FCAC52]';
			default:
				return 'text-[#FFCF96]';
		}
	};

	return (
		<span
			className={twMerge(
				"uppercase text-[20px] font-['Chakra_Petch'] font-bold text-center leading-[110%] tracking-[1px]",
				getTextColorClass()
			)}
		>
			{getWording()}
		</span>
	);
};

const CongratulationsContent = ({
	totalUnclaimedValue,
	prizeValue,
	prizePosition,
}: {
	totalUnclaimedValue: BN;
	prizeValue?: BN;
	prizePosition?: number;
}) => {
	const hasLatestPrize = !!prizeValue;
	const prizeIsFullUnclaimedValue =
		prizeValue && totalUnclaimedValue && totalUnclaimedValue.eq(prizeValue);

	const getPrizeRenderValue = (prize: BN) =>
		Number(BigNum.from(prize, QUOTE_PRECISION_EXP).toNum().toFixed(2));

	// Don't try show the position placement if we didn't win in the most recent round
	const showPosition = hasLatestPrize;
	// If we don't have a latest prize then the total unclaimed value should be the big number
	const valueToHighlight = !hasLatestPrize ? totalUnclaimedValue : prizeValue;
	// If we have a latest prize and extra unclaimed value then also render the total unclaimed at the bottom
	const renderExtraTotalUnclaimedValue =
		totalUnclaimedValue &&
		totalUnclaimedValue.gt(ZERO) &&
		hasLatestPrize &&
		!prizeIsFullUnclaimedValue;

	return (
		<>
			<div className="flex flex-col items-center gap-8 pt-7 grow">
				<div className="text-center font-['Retronoid'] text-[28px] sm:text-[32px] tracking-[6.4px] uppercase text-text-emphasis">
					CONGRATULATIONS,
					<br />
					YOU&apos;VE WON!
				</div>
				<DoubleBorderContainer
					className="border-[#FFCF96] w-[369px] text-center"
					innerClassName="border-[#FFCF96] p-4 bg-[#FFCF9615] backdrop-blur-[2px] flex flex-col items-center"
				>
					{showPosition && <PrizePosition position={prizePosition} />}
					<Countup
						value={getPrizeRenderValue(valueToHighlight)}
						className="mt-2 uppercase text-text-emphasis font-['Chakra_Petch'] text-[44px] leading-[120%] tracking-[2.2px] font-bold"
						decimals={2}
					/>
					<span className="uppercase text-text-emphasis font-['Chakra_Petch'] text-[18px] font-bold">
						USDC
					</span>
					{renderExtraTotalUnclaimedValue && (
						<>
							<Utility.VERTSPACERS />
							<div className="flex space-x-2 text-text-emphasis">
								<span>{`Total Unclaimed Prize Value : `}</span>
								<Countup
									value={getPrizeRenderValue(totalUnclaimedValue)}
									className=""
									decimals={2}
								/>
								<span>{` USDC`}</span>
							</div>
						</>
					)}
				</DoubleBorderContainer>
			</div>
		</>
	);
};

export default function SweepstakeWinnerModal() {
	const setStore = useDriftStore((s) => s.set);
	const safePush = useSafePush();
	const appActions = useDriftActions();

	const ifStakeData = useIfDataStore((s) => s.getIfDataForMarket(0));

	const [
		_isWinner,
		currentPrizeValue,
		currentPrizePosition,
		currentTotalUnclaimedValue,
	] = useSweepstakesStore(
		useShallow((s) => [
			s.competitor.winningDetails.isWinner,
			s.competitor.winningDetails.latestPrizeValue,
			s.competitor.winningDetails.latestPrizePosition,
			s.competitor.winningDetails.totalUnclaimedValue,
		])
	);

	// WE PUT THE INITIAL VALUES INTO A REF WHICH LASTS THE LENGTH OF THE MODAL SO THAT IT DOESN'T CHANGE TO A "BLANK" STATE WHEN THE USER CLAIMS THEIR PRIZE AND THE LIVE VALUES GO BACK TO THEIR DEFAULT STATE
	const initialPrizeValue = useRef(currentPrizeValue);
	const initialPrizePosition = useRef(currentPrizePosition);
	const initialTotalUnclaimedValue = useRef(currentTotalUnclaimedValue);

	const [isIFStakeAgreementDisplayed, setIsIFStakeAgreementDisplayed] =
		useState(false);
	const [hasAcceptedRisks, setHasAcceptedRisks] = useState(false);
	const [hasAcceptedProjectedApr, setHasAcceptedProjectedApr] = useState(false);
	const [hasReadDocsPage, setHasReadDocsPage] = useState(false);
	const [hasSuccessfullyClaimedPrize, setHasSuccessfullyClaimedPrize] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const fireworkRef = useRef<FireworksHandlers>(null);

	// IF stake account states
	const ifStakeAccount = ifStakeData?.userStake; // USDC rewards
	const hasIFStakeAccount = !!(
		ifStakeAccount?.loaded && ifStakeAccount?.account
	);

	const isAgreeBtnDisabled =
		!hasAcceptedRisks || !hasAcceptedProjectedApr || !hasReadDocsPage;

	useEffect(() => {
		if (fireworkRef.current) {
			setTimeout(() => {
				fireworkRef.current?.waitStop();
			}, FIREWORKS_DURATION_MS);
		}
	}, [fireworkRef.current]);

	const onClose = () => {
		setStore((s) => {
			s.modals.showSweepStakeWinnerModal = false;
		});
	};

	const handleNextStep = () => {
		if (hasIFStakeAccount) {
			handleAgree(true);
		} else {
			setIsIFStakeAgreementDisplayed(true);
		}
	};

	async function handleAgree(hasAcceptedIFTermsBefore: boolean) {
		if (
			hasAcceptedIFTermsBefore ||
			(hasAcceptedProjectedApr && hasAcceptedRisks && hasReadDocsPage)
		) {
			try {
				setIsLoading(true);
				const prizeClaimResult = await appActions.claimWinnings(
					DRIFT_COMPETITION_PUBKEY,
					hasIFStakeAccount
				);
				if (prizeClaimResult) {
					setIsIFStakeAgreementDisplayed(false);
					setHasSuccessfullyClaimedPrize(true);
				}
			} finally {
				setIsLoading(false);
			}
		}
	}

	const openIFStakePage = () => {
		onClose();
		safePush(
			(
				BASE_NAVIGATION_OPTIONS.insuranceStaking.handler() as RouteNavigationResult
			).route
		);
	};

	return (
		<Modal onClose={onClose} className="dark-theme" centered>
			<div className="fixed inset-0 z-[110]">
				<Fireworks
					ref={fireworkRef}
					style={{
						width: '100%',
						height: '100%',
						position: 'fixed',
					}}
					options={{
						decay: {
							min: 0.02,
							max: 0.03,
						},
						intensity: 60,
						lineStyle: 'square',
						particles: 80,
						rocketsPoint: {
							min: 35,
							max: 65,
						},
						hue: {
							min: 157,
							max: 360,
						},
					}}
				/>
			</div>
			<Modal.Body
				className="sm:min-w-[491px] sm:max-w-[491px] h-[437px] flex flex-col items-center p-6 relative bg-[#180038] overflow-hidden"
				style={{
					backgroundImage: 'url(/assets/images/sweepstake-winner-bg.png)',
					backgroundSize: '100% 100%',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div
					className="absolute cursor-pointer top-4 right-4 text-text-emphasis"
					onClick={onClose}
				>
					<Close size={24} />
				</div>

				<CongratulationsContent
					totalUnclaimedValue={initialTotalUnclaimedValue.current}
					prizePosition={initialPrizePosition.current}
					prizeValue={initialPrizeValue.current}
				/>

				{hasSuccessfullyClaimedPrize ? (
					<Button.Primary
						size="MEDIUM"
						className="flex items-center w-full gap-[6px] py-3 h-auto bg-button-disabled text-text-emphasis"
						onClick={openIFStakePage}
					>
						<span className="w-[13px] h-[13px] rounded-full bg-positive-green">
							<CheckIcon className="w-full h-full p-[2px]" />
						</span>
						<span>
							Prize deposited to your Insurance Fund Vault.{' '}
							<span className="underline text-interactive-link">View</span>
						</span>
					</Button.Primary>
				) : (
					<Button.Primary
						size="MEDIUM"
						className="w-full h-auto py-3 bg-brand-gradient"
						onClick={handleNextStep}
						disabled={isLoading}
					>
						Claim your prize
					</Button.Primary>
				)}

				{/** Insurance Fund Staking Agreement */}
				<div
					className={twMerge(
						'absolute bottom-0 transition-all p-6 flex flex-col gap-6 rounded-t-2xl bg-container-bg shadow-[0px_-4px_40px_0px_rgba(255,255,255,0.25)]',
						isIFStakeAgreementDisplayed ? 'translate-y-0' : 'translate-y-full'
					)}
				>
					<div className="flex flex-col gap-3">
						<Text.BODY3 className="text-text-default">
							{BigNum.from(
								initialTotalUnclaimedValue.current,
								QUOTE_PRECISION_EXP
							).toNotional()}{' '}
							USDC can be claimed from the protocol-owned Insurance Vault stake,
							and can continue staking or begin unstaking at anytime.
						</Text.BODY3>
						<CheckboxInput
							label="I understand the concept of Insurance Fund Staking on Drift and accept all risks associated with electing to participate in the Insurance Fund, including the possible loss of all funds."
							checked={hasAcceptedRisks}
							onChange={() => setHasAcceptedRisks(!hasAcceptedRisks)}
							secondaryStyle
							alignStart
						/>
						<CheckboxInput
							label="I understand that projected APR is based only on the last hour of revenue pool earnings and is not guaranteed."
							checked={hasAcceptedProjectedApr}
							onChange={() =>
								setHasAcceptedProjectedApr(!hasAcceptedProjectedApr)
							}
							secondaryStyle
							alignStart
						/>
						<CheckboxInput
							label={<>I have read the Insurance Fund Staking</>}
							linkElement={
								<a
									href="https://docs.drift.trade/insurance-fund/insurance-fund-intro"
									target="_blank"
									rel="noreferrer"
									className="ml-[2px]"
								>
									docs page
								</a>
							}
							checked={hasReadDocsPage}
							onChange={() => setHasReadDocsPage(!hasReadDocsPage)}
							secondaryStyle
							alignStart
						/>
					</div>
					<Button.Primary
						size="MEDIUM"
						className="w-full"
						onClick={() => handleAgree(false)}
						disabled={isAgreeBtnDisabled || isLoading}
					>
						Agree and Continue
					</Button.Primary>
				</div>
			</Modal.Body>
		</Modal>
	);
}
